#!/usr/bin/env node
/**
 * BaskReport AI 报表生成器 CLI 入口。
 * 子命令：generate / datasource / report / template / install / uninstall。
 */

import { Command } from 'commander';
import { registerGenerate } from './generate.js';
import { registerDatasource } from './datasource.js';
import { registerReport } from './report.js';
import { registerTemplate } from './template.js';
import { registerInstall } from './install.js';
import { registerUninstall } from './uninstall.js';
import { applyConfigDir, setConfigDir } from '../core/config.js';
import { resetBudget, resolveBudgetLimit, DEFAULT_BUDGET_TOKENS } from '../core/budget.js';
import { getLogger } from '../core/logger.js';

const log = getLogger('cli:index');

const program = new Command();
program
  .name('baskreport')
  .description('BaskReport AI 报表生成器：JSON 中间层 → XML → 服务端/离线文件')
  .version('1.0.2')
  .option(
    '--config-dir <dir>',
    '显式指定配置查找目录（含 .env / baskreport.config.json）；AI client 注入最可靠，优先级高于 cwd 自动探测',
  )
  // —— token 预算熔断（会话累计）——
  .option(
    '--max-tokens <n>',
    '单次报表任务的累计 token 预算上限（默认 500000）；超预算后命令硬阻断并以非零码退出',
  )
  .option('--budget-reset', '清零 <状态目录>/.baskreport/budget.json 状态文件，开启新会话后退出')
  .option('--no-budget', '关闭 token 预算熔断（应急逃生口，仅用户明确要求时使用）');

registerGenerate(program);
registerDatasource(program);
registerReport(program);
registerTemplate(program);
registerInstall(program);
registerUninstall(program);

// 全局 --budget-reset：独立于子命令即可清零累计预算并退出（开新会话）。
// 用 option 事件处理，既支持「baskreport --budget-reset」（无子命令）也支持「--budget-reset <子命令>」。
// 注意：Commander ≥12 的 option 事件名是「option:<长选项名>」（连字符形式，如 option:budget-reset），
// ≤11 才是 camelCase（option:budgetReset）；此处两者都监听以兼容不同版本。
for (const ev of ['option:budget-reset', 'option:budgetReset'] as const) {
  program.on(ev, () => {
    // 把 --max-tokens 注入 env，使 resolveBudgetLimit 能取到 CLI 指定上限
    const o = program.opts() as { maxTokens?: string };
    if (o.maxTokens) process.env.BUDGET_LIMIT_CLI = String(o.maxTokens);
    resetBudget(resolveBudgetLimit());
    process.exit(0);
  });
}

// 解析前预扫描 --config-dir（兼容 `--config-dir <dir>` 与 `--config-dir=<dir>`）：
// commander 的 option 事件在 parse 途中触发（如 option:budget-reset 先于其他选项解析），
// 而预算/日志状态目录在首个日志输出时即被读取；提前注入可保证
// --budget-reset 清零、预算记账、日志落盘都落在用户指定的状态目录。
for (let i = 0; i < process.argv.length - 1; i++) {
  const a = process.argv[i];
  if (a === '--config-dir') {
    const dir = process.argv[i + 1]?.trim();
    if (dir) setConfigDir(dir);
    break;
  }
  if (a?.startsWith('--config-dir=')) {
    const dir = a.slice('--config-dir='.length).trim();
    if (dir) setConfigDir(dir);
    break;
  }
}
// 在解析后立即把全局 --config-dir 注入配置查找目录（子命令 action 也会再次调用，幂等）。
applyConfigDir(program.opts() as { configDir?: string });

// 把全局预算选项注入 env，供 src/core/budget.ts 的 resolveBudgetLimit 读取
// （优先级：CLI --max-tokens > env BASK_MAX_TOKENS > config > 默认）。
const globalOpts = program.opts() as { maxTokens?: string };
if (globalOpts.maxTokens) {
  process.env.BUDGET_LIMIT_CLI = String(globalOpts.maxTokens);
}

program.parseAsync(process.argv).catch((e) => {
  log.error(`命令执行失败：${e?.message ?? e}`, e);
  process.exit(1);
});
